import { STREAM_UI_CONFIG } from '../consts';

/**
 * Mode for calculating focus position within a range
 */
export type FocusMode = 'center' | 'start' | 'end';

/**
 * Configuration for focus calculation
 */
export interface FocusCalculationConfig {
  /**
   * Mode specifying where the focus should be positioned within the range
   * - 'center': focus is centered in the range (default behavior)
   * - 'start': focus starts at the beginning of the range
   * - 'end': focus ends at the end of the range
   */
  mode?: FocusMode;
  /**
   * Length of the focus in minutes
   * Defaults to STREAM_UI_CONFIG.defaultFocusLengthMins
   */
  length?: number;
}

/**
 * Strategy functions for calculating focus based on different modes
 */
type FocusCalculationStrategy = (
  range: [number, number],
  focusLengthMs: number,
) => [number, number];

/**
 * Calculates focus at the center of the range
 */
const calculateCenterFocus: FocusCalculationStrategy = (range, focusLengthMs) => {
  const [from, to] = range;
  const rangeCenter = from + (to - from) / 2;
  const focusFrom = rangeCenter - focusLengthMs / 2;
  const focusTo = rangeCenter + focusLengthMs / 2;
  return [focusFrom, focusTo];
};

/**
 * Calculates focus starting at the beginning of the range
 */
const calculateStartFocus: FocusCalculationStrategy = (range, focusLengthMs) => {
  const [from] = range;
  const focusFrom = from;
  const focusTo = from + focusLengthMs;
  return [focusFrom, focusTo];
};

/**
 * Calculates focus ending at the end of the range
 */
const calculateEndFocus: FocusCalculationStrategy = (range, focusLengthMs) => {
  const [, to] = range;
  const focusFrom = to - focusLengthMs;
  const focusTo = to;
  return [focusFrom, focusTo];
};

/**
 * Strategy map for focus calculation modes
 */
const focusStrategies: Record<FocusMode, FocusCalculationStrategy> = {
  center: calculateCenterFocus,
  start: calculateStartFocus,
  end: calculateEndFocus,
};

/**
 * Calculates focus range from a given range using a strategy pattern.
 * The strategy is determined by the mode parameter (center, start, or end).
 *
 * @param range - The time range [from, to] in milliseconds
 * @param config - Configuration object with mode and length parameters
 * @returns The calculated focus range [focusFrom, focusTo] in milliseconds
 */
export function calculateFocusFromRange(
  range: [number, number],
  config: FocusCalculationConfig = {},
): [number, number] {
  const mode: FocusMode = config.mode ?? 'start';
  const lengthMins = config.length ?? STREAM_UI_CONFIG.defaultFocusLengthMins;
  const focusLengthMs = lengthMins * 60 * 1000;

  const strategy = focusStrategies[mode];
  return strategy(range, focusLengthMs);
}
